import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Action, Alert, Badge, Modal, SupportingText } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import MediaUploader from '~/components/field/MediaUploader';
import { MediaGallery } from '~/components/media/MediaGallery';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useStoreId } from '~/providers/StoreUserProvider';
import { formatter } from 'corso-web-lib';
import LineItemDisclosure from './LineItemDisclosure';

type ClaimLineItemMedia = NonNullable<CrewMerchantUi.ClaimLineItem['media']>;

const toAssets = (media: ClaimLineItemMedia) =>
  media.map(({ filename, url }) => ({ name: filename, src: url }));

const editSchema = z.object({
  images: z.array(z.object({ name: z.string(), src: z.string() })).optional(),
});

function CustomerMediaEdit({
  claimLineItem,
  onClose,
}: {
  claimLineItem: CrewMerchantUi.ClaimLineItem;
  onClose: () => void;
}) {
  const { id, media = [] } = claimLineItem;
  const storeId = useStoreId();
  const claim = useClaim();

  const lineItemApi = api.store(storeId).claim(`${claim.id}`).lineItem(`${id}`);

  const {
    mutateAsync: saveMedia,
    isPending,
    isError,
  } = useMutation({
    mutationFn: async ({ images = [] }: z.infer<typeof editSchema>) => {
      const nextFilenames = new Set(images.map(({ name }) => name));
      const prevFilenames = new Set(media.map(({ filename }) => filename));

      const additions = images.filter(({ name }) => !prevFilenames.has(name));
      const removals = media.filter(
        ({ filename }) => !nextFilenames.has(filename),
      );

      await Promise.all([
        ...additions.map(({ name }) =>
          lineItemApi.media.create({ filename: name }),
        ),
        ...removals.map(({ claimLineItemImageId }) =>
          lineItemApi.media.remove(`${claimLineItemImageId}`),
        ),
      ]);
    },
    onSuccess: () => claim.refetch(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<z.infer<typeof editSchema>>({
    resolver: zodResolver(editSchema),
    defaultValues: { images: toAssets(media) },
  });

  const onSubmit: FormEventHandler = (event) => {
    handleSubmit(async (values) => {
      await saveMedia(values);
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal title="Edit Customer Media" onClose={onClose} show>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {isDirty && (
          <Alert variant="warning" heading="Unsaved Changes">
            Click Save to apply them.
          </Alert>
        )}
        <Controller
          control={control}
          name="images"
          render={({ field: { value, onChange } }) => (
            <MediaUploader
              id="claim-line-item-media-uploader"
              label="Media"
              labelVisuallyHidden
              uploadPath="claimLineItem"
              assets={value}
              onChange={onChange}
            />
          )}
        />
        {isError && (
          <SupportingText error>
            There was an error saving media. Please try again.
          </SupportingText>
        )}
        <Action variant="primary" type="submit" loading={isPending}>
          Save
        </Action>
      </form>
    </Modal>
  );
}

export default function CustomerMedia({
  claimLineItem,
}: {
  claimLineItem: CrewMerchantUi.ClaimLineItem;
}) {
  const { media = [] } = claimLineItem;
  const [showForm, setShowForm] = useState(false);

  return (
    // TODO: a Plus button next to the formatter badge would be more on brand to the app probably?
    <LineItemDisclosure
      defaultOpen={media.length > 0}
      title="Customer Media"
      supportingText={<Badge>{formatter.count(media.length)}</Badge>}
    >
      <MediaGallery
        title="Customer Media"
        items={toAssets(media).map((asset, index) => ({
          src: asset.src,
          alt: `Customer Media ${index + 1}`,
        }))}
      />
      <div className="self-end">
        <Action variant="primary" onClick={() => setShowForm(true)}>
          {media.length > 0 ? 'Edit Media' : 'Add Media'}
        </Action>
      </div>
      {showForm && (
        <CustomerMediaEdit
          claimLineItem={claimLineItem}
          onClose={() => setShowForm(false)}
        />
      )}
    </LineItemDisclosure>
  );
}
